/**
 * FeeSplitter chain configuration for the /rewards page.
 * Maps chain IDs to deployed FeeSplitter contract addresses and USDC tokens.
 */

const IS_TESTNET = import.meta.env.VITE_NETWORK === 'testnet' ||
  !import.meta.env.VITE_NETWORK; // default to testnet

type FeeSplitterChainConfig = Record<number, {
  splitterAddress: `0x${string}`;
  usdcAddress: `0x${string}`;
  chainName: string;
  usdcDecimals: number;
  explorerUrl: string;
}>;

export const FEESPLITTER_CHAINS: FeeSplitterChainConfig = IS_TESTNET ? {
  11155111: {
    splitterAddress: '0xe2dd763ae94ca7e10c674321a58aaaa48dacb54f',
    usdcAddress: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238',
    chainName: 'Eth',
    usdcDecimals: 6,
    explorerUrl: 'https://sepolia.etherscan.io',
  },
  84532: {
    splitterAddress: '0x1c5b55996dcecf512834cc16c5b2d7d1b49c7b1a',
    usdcAddress: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',
    chainName: 'Base',
    usdcDecimals: 6,
    explorerUrl: 'https://sepolia.basescan.org',
  },
} : {
  1: {
    splitterAddress: '0xa339d56e2f7aebeaaa4e2bac877cf5d9f70edba9',
    usdcAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    chainName: 'Eth',
    usdcDecimals: 6,
    explorerUrl: 'https://etherscan.io',
  },
  8453: {
    splitterAddress: '0x4d679c799251b95c8e1775acae21181f699913b0',
    usdcAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    chainName: 'Base',
    usdcDecimals: 6,
    explorerUrl: 'https://basescan.org',
  },
  10: {
    splitterAddress: '0x5abde2f811dd2801da274fbec1c8438acf3a5bc1',
    usdcAddress: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
    chainName: 'OP',
    usdcDecimals: 6,
    explorerUrl: 'https://optimistic.etherscan.io',
  },
  42161: {
    // NB: same address as the polygon-amoy TESTNET FBV01 bridge (see @parse/shared) — a legitimate
    // same-deployer (0x7d77) + same-nonce CREATE collision across chains, NOT a copy-paste. Verified
    // on-chain: a real Arbitrum FeeSplitter (11443B, owner 0x7d77, USDC allowed). Do not "correct".
    splitterAddress: '0x8175c8acd1c4fd8b2550e668c716e90cce481a34',
    usdcAddress: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
    chainName: 'Arb',
    usdcDecimals: 6,
    explorerUrl: 'https://arbiscan.io',
  },
  137: {
    splitterAddress: '0xef312596370f0e3bb3be1232091dc6aaf5bb587f',
    usdcAddress: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
    chainName: 'Poly',
    usdcDecimals: 6,
    explorerUrl: 'https://polygonscan.com',
  },
  56: {
    splitterAddress: '0x1fc8a13046b4d01b480fb17455e6ca66d1e49666',
    usdcAddress: '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d',
    chainName: 'BNB',
    usdcDecimals: 18, // BSC USDC is 18 decimals (NOT 6 like the other chains)
    explorerUrl: 'https://bscscan.com',
  },
};

/**
 * Maps API chain names (from earnings endpoint) to EVM chain IDs.
 * On testnet, "ethereum" maps to Sepolia, "base" to Base Sepolia, etc.
 */
export const CHAIN_NAME_TO_ID: Record<string, number> = IS_TESTNET
  ? {
      ethereum: 11155111,  // Sepolia
      base: 84532,         // Base Sepolia
      optimism: 11155420,  // OP Sepolia
      arbitrum: 421614,    // Arbitrum Sepolia
      polygon: 80002,      // Amoy
      solana: 7565164,
    }
  : {
      ethereum: 1,
      base: 8453,
      optimism: 10,
      arbitrum: 42161,
      polygon: 137,
      bnb: 56,
      solana: 7565164,
    };

/** Short (≤4 char) chain labels for the earnings UI, keyed by API chain name. */
export const CHAIN_SHORT_NAME: Record<string, string> = {
  ethereum: 'Eth', base: 'Base', optimism: 'OP', arbitrum: 'Arb',
  polygon: 'Poly', bnb: 'BNB', solana: 'Sol', tron: 'Tron',
}

/** Short chain labels keyed by numeric chain ID (mainnet + testnet + Solana). */
export const CHAIN_ID_TO_SHORT: Record<number, string> = {
  1: 'Eth', 8453: 'Base', 10: 'OP', 42161: 'Arb', 137: 'Poly', 56: 'BNB', 7565164: 'Sol',
  11155111: 'Eth', 84532: 'Base', 11155420: 'OP', 421614: 'Arb', 80002: 'Poly', 97: 'BNB',
}

/** Native gas-token symbol per chain ID. */
export const NATIVE_SYMBOL_BY_CHAIN_ID: Record<number, string> = {
  1: 'ETH', 8453: 'ETH', 10: 'ETH', 42161: 'ETH', 137: 'POL', 56: 'BNB', 7565164: 'SOL',
  11155111: 'ETH', 84532: 'ETH', 11155420: 'ETH', 421614: 'ETH', 80002: 'POL', 97: 'BNB',
}
